import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  CREATE_PRODUCT, PRODUCT_UPDATE, UPLOAD_PRODUCT_MEDIA_BASE64,
  GET_PRODUCT_WITH_VARIANTS, GET_CURRENT_USER,
  type ManageProductWithVariants, type ManageCurrentUser,
} from '../../graphql/management'
import { useAuth } from '../../auth/AuthContext'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

// Create or edit a product. Create needs a mallId (from the session, falling
// back to a currentUser fetch). On create we redirect to edit so images +
// variants can be added (the two-step the API implies). Mirrors iOS
// ProductCreationView / EditProductView.
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result as string)
    r.onerror = () => reject(r.error)
    r.readAsDataURL(file)
  })
}

export default function ProductEditor() {
  const { id } = useParams()
  const editing = Boolean(id)
  const navigate = useNavigate()
  const fb = useFormFeedback()
  const { user } = useAuth()

  // mallId for create — prefer the session, else fetch currentUser.
  const { data: meData } = useQuery<ManageCurrentUser>(GET_CURRENT_USER, { skip: editing || !!user?.mallId })
  const mallId = user?.mallId ?? meData?.currentUser?.mall?.id ?? null

  const { data, loading, refetch } = useQuery<ManageProductWithVariants>(GET_PRODUCT_WITH_VARIANTS, {
    variables: { id }, skip: !editing, fetchPolicy: 'cache-and-network',
  })
  const [createProduct, { loading: creating }] = useMutation(CREATE_PRODUCT)
  const [updateProduct, { loading: updating }] = useMutation(PRODUCT_UPDATE)
  const [uploadMedia, { loading: uploading }] = useMutation(UPLOAD_PRODUCT_MEDIA_BASE64)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [vendor, setVendor] = useState('')
  const [productType, setProductType] = useState('')
  const [tags, setTags] = useState('')
  const [price, setPrice] = useState('')

  const product = data?.product
  useEffect(() => {
    if (!product) return
    setTitle(product.title); setDescription(product.description ?? '')
    setVendor(product.vendor ?? ''); setProductType(product.productType ?? '')
    // Backend `tags` is already a comma-joined String (not an array — that's the
    // separate `tagsList` field). Seed the comma-separated input directly.
    setTags(product.tags ?? '')
  }, [product])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault(); fb.reset()
    try {
      if (editing) {
        const { data } = await updateProduct({ variables: { id, title, description, vendor, productType, tags } })
        if (!fb.fromUserErrors(data?.productUpdate?.userErrors as UserError[] | undefined)) return
        fb.setSuccess('Saved.')
      } else {
        if (!mallId) { fb.setError('No mall on your account — can’t create products.'); return }
        const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean)
        const { data } = await createProduct({
          variables: { mallId, title, price: parseFloat(price) || 0, description, vendor, productType, tags: tagList, status: 'active' },
        })
        if (!fb.fromUserErrors(data?.createProduct?.userErrors as UserError[] | undefined)) return
        const newId = data?.createProduct?.product?.id
        if (newId) navigate(`/manage/products/${newId}/edit`)
        else navigate('/manage/products')
      }
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
  }

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    fb.reset()
    try {
      const tooBig = files.find((f) => f.size > 6 * 1024 * 1024)
      if (tooBig) { fb.setError(`${tooBig.name} is over 6MB.`); return }
      const images = await Promise.all(files.map(fileToDataUrl))
      const { data } = await uploadMedia({ variables: { productId: id, images } })
      if (!fb.fromUserErrors(data?.uploadProductMediaBase64?.userErrors as UserError[] | undefined)) return
      await refetch(); fb.setSuccess('Images uploaded.')
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
    finally { e.target.value = '' }
  }

  if (editing && loading && !product) return <div className="muted">Loading product…</div>
  const saving = creating || updating

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>{editing ? `Edit · ${product?.title ?? ''}` : 'New product'}</h1>
        <button className="btn-link" onClick={() => navigate('/manage/products')}>← Back</button>
      </header>

      <form className="create-form" onSubmit={onSubmit}>
        <label><span>Title</span><input value={title} onChange={(e) => setTitle(e.target.value)} required /></label>
        {!editing && (
          <label><span>Price</span><input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" required /></label>
        )}
        <label><span>Description</span><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} /></label>
        <label><span>Vendor</span><input value={vendor} onChange={(e) => setVendor(e.target.value)} /></label>
        <label><span>Type</span><input value={productType} onChange={(e) => setProductType(e.target.value)} /></label>
        <label><span>Tags (comma-separated)</span><input value={tags} onChange={(e) => setTags(e.target.value)} /></label>
        <Feedback error={fb.error} success={fb.success} />
        <div className="form-actions">
          <button className="btn-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save product' : 'Create product'}</button>
          {editing && <Link className="btn-secondary" to={`/manage/products/${id}/variants`}>Manage variants</Link>}
          {editing && <Link className="btn-secondary" to={`/manage/products/${id}/assign`}>Assign to stores</Link>}
        </div>
      </form>

      {editing && (
        <section className="product-media">
          <h2 className="manage-subhead">Images</h2>
          <div className="product-media-thumbs">
            {(product?.imageUrls ?? []).map((u) => <img key={u} src={u} alt="" className="product-mgmt-thumb" />)}
          </div>
          <label className="btn-secondary file-btn">
            {uploading ? 'Uploading…' : 'Upload images'}
            <input type="file" accept="image/*" multiple onChange={onUpload} hidden disabled={uploading} />
          </label>
        </section>
      )}
    </div>
  )
}
